import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Brand, Product } from '@hygh-vybe/lib';
import { SEO } from '../components/SEO';
import { ProductCard } from '../components/ProductCard';
import './BrandPage.css';
import { API_BASE, CATALOG_URL } from '../config';

/** Convert a slug back to a comparable form for matching brand names. */
function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function BrandPage() {
  const { slug } = useParams<{ slug: string }>();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    async function fetchBrandData() {
      setLoading(true);
      setError(null);

      try {
        // Fetch all brands and match by slug
        const brandsRes = await fetch(`${API_BASE}/v1/brands`);
        if (!brandsRes.ok) throw new Error('Failed to load brands');
        const brandsJson = await brandsRes.json();
        const allBrands = (brandsJson.data || []) as Brand[];

        const matchedBrand = allBrands.find(
          (b) => slugify(b.brandName) === slug,
        );

        if (!matchedBrand) {
          setError('Brand not found');
          setLoading(false);
          return;
        }

        setBrand(matchedBrand);

        // Fetch products from static catalog and filter by brand
        const catalogRes = await fetch(CATALOG_URL);
        if (catalogRes.ok) {
          const catalogJson = await catalogRes.json();
          const allProducts = (catalogJson.products || []) as Product[];
          const brandProducts = allProducts.filter(
            (p) => p.brandName === matchedBrand.brandName,
          );
          setProducts(brandProducts);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Something went wrong');
      } finally {
        setLoading(false);
      }
    }

    fetchBrandData();
  }, [slug]);

  if (loading) {
    return (
      <div className="brand-page">
        <div className="brand-page__loading">Loading brand...</div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="brand-page">
        <div className="brand-page__error">
          <h1>Brand Not Found</h1>
          <p>We couldn't find a brand matching that URL.</p>
          <Link to="/thc-drinks" className="brand-page__back-link">Browse all drinks</Link>
        </div>
      </div>
    );
  }

  const brandJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Brand',
    name: brand.brandName,
    ...(brand.logoUrl ? { logo: brand.logoUrl } : {}),
    ...(brand.websiteUrl ? { url: brand.websiteUrl } : {}),
    description: brand.description || `${brand.brandName} THC beverages — discover and buy directly.`,
  };

  return (
    <div className="brand-page">
      <SEO
        title={`${brand.brandName} THC Drinks - Buy ${brand.brandName} Beverages`}
        description={brand.description || `Browse all ${brand.brandName} THC beverages. Real ratings, honest reviews, and buy links direct from the brand.`}
        canonical={`/brands/${slug}`}
        image={brand.logoUrl}
        jsonLd={brandJsonLd}
      />

      {/* Brand Header */}
      <header className="brand-page__header">
        {brand.logoUrl && (
          <img
            src={brand.logoUrl}
            alt={brand.brandName}
            className="brand-page__logo"
          />
        )}
        <div className="brand-page__info">
          <h1 className="brand-page__name">{brand.brandName}</h1>
          {brand.location && (
            <span className="brand-page__location">{brand.location}</span>
          )}
          {brand.description && (
            <p className="brand-page__desc">{brand.description}</p>
          )}
          <div className="brand-page__actions">
            {brand.buyWebsiteUrl && (
              <a
                href={brand.buyWebsiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="brand-page__cta brand-page__cta--buy"
              >
                Buy from {brand.brandName}
              </a>
            )}
            {brand.websiteUrl && (
              <a
                href={brand.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="brand-page__cta brand-page__cta--secondary"
              >
                Visit Website
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Products Grid */}
      {products.length > 0 && (
        <section className="brand-page__products">
          <h2 className="brand-page__section-title">
            {brand.brandName} Products ({products.length})
          </h2>
          <div className="brand-page__grid">
            {products.map((product) => (
              <ProductCard key={product.productId} product={product} />
            ))}
          </div>
        </section>
      )}

      {products.length === 0 && (
        <section className="brand-page__products">
          <p className="brand-page__empty">No products found for this brand yet.</p>
        </section>
      )}
    </div>
  );
}
